import * as i0 from '@angular/core';
import { Injectable, Component, Input, Directive, EventEmitter, ElementRef, Output, ContentChildren, ViewChild, NgModule } from '@angular/core';
import { BehaviorSubject, Subject, Observable, forkJoin } from 'rxjs';
import * as i2 from '@angular/platform-browser';
import * as i3 from '@appkit4/angular-api';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import createFocusTrap from 'focus-trap';
import { convertToRem, convertToRelativePx } from '@appkit4/angular-components/utils';
import { DomPortal } from '@angular/cdk/portal';
import * as i2$1 from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';

class ModalContainerService {
    constructor() {
        this.modals = [];
    }
    add(modal) {
        // add modal to array of active modals
        this.modals.push(modal);
    }
    remove(id) {
        // remove modal from array of active modals
        this.modals = this.modals.filter(x => x.id !== id);
    }
    open(id) {
        // open modal specified by id
        let modal = this.modals.filter(x => x.id === id)[0];
        if (modal) {
            modal.open();
            return new BehaviorSubject('Open success');
        }
        else {
            console.error('modal with id: "' + id + '" is not exist');
            return new BehaviorSubject(false);
        }
    }
    close(id) {
        // close modal specified by id
        let modal = this.modals.filter(x => x.id === id)[0];
        if (modal) {
            modal.close();
            return new BehaviorSubject('Close success');
        }
        else {
            console.error('modal with id: "' + id + '" is not exist');
            return new BehaviorSubject(false);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalContainerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalContainerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalContainerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ModalContainerComponent {
    constructor(modalService, el) {
        this.modalService = modalService;
        this.el = el;
        this.id = '';
        this.initialFocusIndex = 0;
        this.element = el.nativeElement;
    }
    ngOnInit() {
        let modal = this;
        // ensure id attribute exists
        if (!this.id) {
            console.error('modal must have an id');
            return;
        }
        // Attach modal to attachedDOM if it exists, else attach modal to body which is default behavior
        if (this.attachedDOM) {
            if (this.attachedDOM === 'body') {
                document.body.appendChild(this.element);
            }
            else {
                if (this.isElement(this.attachedDOM)) {
                    this.attachedDOM.appendChild(this.element);
                }
                else if (this.attachedDOM.el && this.attachedDOM.el.nativeElement) {
                    this.attachedDOM.el.nativeElement.appendChild(this.element);
                }
                else {
                    throw new Error(`
                        cannot append ${this.element} to ${this.attachedDOM};
                    `);
                }
            }
        }
        else {
            // move element to bottom of page (just before </body>) so it can be displayed above everything else
            document.body.appendChild(this.element);
        }
        // close modal on background click
        // this.element.addEventListener('click', function (e: any) {
        //     if (e.target && (e.target.className.includes('modal ') || e.target.parentNode.className.includes('modal '))) {
        //         modal.close();
        //     }
        // });
        // add self (this modal instance) to the modal service so it's accessible from controllers
        this.modalService.add(this);
        this.keyupEvent = (e) => this.tabEvent(e);
        // comment the below line because it impact the function of 'shift + tab' action
        // this.el.nativeElement.addEventListener('keyup', this.keyupEvent);
    }
    isElement(target) {
        if (typeof HTMLElement === 'object') {
            return target instanceof HTMLElement;
        }
        else {
            return target && typeof target === 'object' && target !== null && target.nodeType === 1 && typeof target.nodeName === 'string';
        }
    }
    tabEvent(e) {
        if (e.keyCode === 9) {
            const focusable = this.el.nativeElement.querySelectorAll('button, [href], input, select, textarea, li, a,[tabindex]:not([tabindex="-1"])');
            this.nextActiveElement?.focus();
            this.nextActiveElement = this.nextActiveElementEvent(focusable, [].indexOf.call(focusable, document.activeElement));
            if (!focusable || focusable?.length === 0)
                return;
            this.setFocusableOutline(focusable);
        }
    }
    setFocusableOutline(focusable) {
        if (focusable.length <= 1)
            return;
        if (this.nextActiveElement)
            focusable[1].style.outline = 'none';
        else
            focusable[1].style.outline = this.focusStyle;
    }
    nextActiveElementEvent(elements, currentIndex) {
        if (currentIndex + 1 >= elements.length) {
            return elements[0];
        }
        else
            return null;
    }
    // remove self from modal service when directive is destroyed
    ngOnDestroy() {
        Element.prototype.remove = function () {
            if (this.parentNode) {
                this.parentNode.removeChild(this);
            }
        };
        this.modalService.remove(this.id);
        this.element.remove();
        // this.el.nativeElement.removeEventListener('keyup', this.keyupEvent);
    }
    // open modal
    open() {
        const modalElement = this.el.nativeElement.querySelector('.ap-modal');
        if (this.ariaLabelLedby?.trim() && modalElement) {
            const mockLabeledbyElement = modalElement.querySelector('#labelledbyId');
            if (mockLabeledbyElement)
                mockLabeledbyElement.textContent = document.querySelector(`#${this.ariaLabelLedby}`)?.textContent || '';
        }
        else if (modalElement && !this.ariaLabelLedby) {
            modalElement.removeAttribute('aria-labelledby');
        }
        const trigger = window.event?.type;
        const setTimeObj = setTimeout(() => {
            if (this.element.children[0])
                this.element.children[0].classList.add('show');
            clearTimeout(setTimeObj);
        });
        if (this.element && this.element.style
            && this.element.children && this.element.children.length > 0) {
            this.element.style.display = 'block';
            this.element.children[0].display = 'block';
        }
        if (document.body && this.blockScroll)
            document.body.classList.add('modal-open');
        this.activeElement = document.activeElement;
        setTimeout(() => {
            const focusable = this.el.nativeElement?.querySelectorAll('button:not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([tabindex="-1"]), select:not([tabindex="-1"]), textarea:not([tabindex="-1"]), a:not([tabindex="-1"]),[tabindex]:not([tabindex="-1"])');
            if (focusable && focusable.length > 0) {
                this.firstFocusableFocus(focusable);
                if (this.el.nativeElement) {
                    let focusIndex = this.initialFocusIndex >= 0 ? this.initialFocusIndex : focusable.length + this.initialFocusIndex;
                    const focusTrap = createFocusTrap(this.el.nativeElement, {
                        initialFocus: focusable[focusIndex] || focusable[0],
                        clickOutsideDeactivates: true,
                        escapeDeactivates: false
                    });
                    if (trigger === 'keydown' || trigger === 'keyup' || trigger === 'keypress') {
                        // Add 'keyboard-focus' to all appkit buttons when open with keyboard
                        this.el.nativeElement.querySelectorAll('.ap-button')?.forEach((btnELe) => {
                            btnELe.classList.add('keyboard-focus');
                        });
                        // Add 'keyboard-focus' to all appkit dropdowns and input-fields when open with keyboard
                        this.el.nativeElement.querySelectorAll('.ap-field-wrapper')?.forEach((dropdownELe) => {
                            dropdownELe.classList.add('keyboard-focus');
                        });
                        // Add 'keyboard-focus' to all appkit searchs when open with keyboard
                        this.el.nativeElement.querySelectorAll('.ap-search-input')?.forEach((searchELe) => {
                            searchELe.classList.add('keyboard-focus');
                        });
                        // Add 'keyboard-focus' to all appkit avatars when open with keyboard
                        this.el.nativeElement.querySelectorAll('.ap-avatar')?.forEach((avatrELe) => {
                            avatrELe.classList.add('keyboard-focus');
                        });
                    }
                    focusTrap.activate();
                }
            }
        }, 300); // wait for the transition time of visibility
        let headerEle = this.el.nativeElement.querySelector('.ap-modal-header');
        let iconsContainer = this.el.nativeElement.querySelector('.ap-modal-header-icons-container');
        if (headerEle && iconsContainer) {
            headerEle.style.paddingRight = convertToRem(iconsContainer.getBoundingClientRect().width + convertToRelativePx(12));
        }
    }
    firstFocusableFocus(focusable) {
        const firstFocusable = focusable[0];
        this.focusStyle = firstFocusable.style.outline;
        firstFocusable.focus();
    }
    // close modal
    close() {
        const trigger = window.event?.type;
        const setTimeObj = setTimeout(() => {
            if (document.body && this.blockScroll)
                document.body.classList.remove('modal-open');
            setTimeout(() => {
                this.element.style.display = 'none';
            }, 300);
            clearTimeout(setTimeObj);
        });
        if (this.element && this.element.children
            && this.element.children.length > 0) {
            this.element.children[0].classList.remove('show');
        }
        // this.enterEvent = (e: any) => this.focusEvent(e);
        // window.document.addEventListener('keyup', this.enterEvent);
        setTimeout(() => {
            if (trigger === 'keydown' || trigger === 'keyup' || trigger === 'keypress') {
                // Add 'keyboard-focus' to appkit trigger button when close with keyboard
                if (this.activeElement && (this.activeElement.classList.value.indexOf('ap-button') > -1)) {
                    this.activeElement.classList.add('keyboard-focus');
                }
            }
            if (this.activeElement && document.activeElement) {
                this.activeElement.focus();
            }
        }, 400);
    }
    focusEvent(e) {
        // Only focus trigger button when close
        setTimeout(() => {
            if (e.keyCode === 13 && this.activeElement && document.activeElement
                && (['body', 'button'].indexOf(document.activeElement.tagName.toLowerCase()) > -1)) {
                this.activeElement.focus();
            }
            window.document.removeEventListener('keyup', this.enterEvent);
        }, 350);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalContainerComponent, deps: [{ token: ModalContainerService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.11", type: ModalContainerComponent, selector: "modal", inputs: { id: "id", initialFocusIndex: "initialFocusIndex", blockScroll: "blockScroll", attachedDOM: "attachedDOM", ariaLabelLedby: "ariaLabelLedby" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'modal',
                    template: '<ng-content></ng-content>'
                }]
        }], ctorParameters: () => [{ type: ModalContainerService }, { type: i0.ElementRef }], propDecorators: { id: [{
                type: Input
            }], initialFocusIndex: [{
                type: Input
            }], blockScroll: [{
                type: Input
            }], attachedDOM: [{
                type: Input
            }], ariaLabelLedby: [{
                type: Input
            }] } });

class CustomizeTemplate {
    constructor(template) {
        this.template = template;
        this.type = '';
        this.name = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CustomizeTemplate, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.11", type: CustomizeTemplate, selector: "[ngTemplate]", inputs: { type: "type", name: ["ngTemplate", "name"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: CustomizeTemplate, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngTemplate]',
                    host: {}
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }], propDecorators: { type: [{
                type: Input
            }], name: [{
                type: Input,
                args: ['ngTemplate']
            }] } });
class ModalComponent {
    constructor(applicationRef, modalContainerService, componentFactoryResolver, injector, renderer, sanitizer, localizationConfig) {
        this.applicationRef = applicationRef;
        this.modalContainerService = modalContainerService;
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this.renderer = renderer;
        this.sanitizer = sanitizer;
        this.localizationConfig = localizationConfig;
        this.appModalId = '';
        this.title = '';
        this.content = '';
        this.maskCloseable = true;
        this.closable = true;
        this.style = '';
        this.styleClass = '';
        this.contentStyle = '';
        this.contentStyleClass = '';
        this.footerStyle = '';
        this.closeOnPressEscape = true;
        this.alignment = 'center';
        this.initialFocusIndex = 0;
        this.ariaLabel = "";
        this.closeTabindex = "0";
        this.blockScroll = true;
        // @Output() onCheckModalClose: EventEmitter<any> = new EventEmitter<any>();
        this.onCheckModalOk = new EventEmitter();
        this.onClose = new EventEmitter();
        this.onOpen = new EventEmitter();
        this.onScrollModal = new EventEmitter();
        this.serviceCall = false;
        this.needOutlineStyle = true;
        this.wrapperStyle = {};
        this.lableById = "";
        this.currentAriaLabel = '';
        this.closeValue = new Subject();
        this.modalStyle = {};
        this.contentStyleObj = {};
        this.footerStyleObj = {};
        /**
         * Creates an instance of ModalComponent.
         * @param {ModalContainerService} modalContainerService
         * @param {ComponentFactoryResolver} componentFactoryResolver
         * @param {Injector} injector
         * @memberof ModalComponent
         */
        this.globalListenFunc = () => { };
    }
    ngOnInit() {
        /** Avoid mutliple modal trigger close method */
        // if (this.closeOnPressEscape) {
        //   this.globalListenFunc = this.renderer.listen(
        //   'document', 'keydown', (event: KeyboardEvent) => {
        //     event.keyCode === ESCAPE && this.close(this.appModalId)
        //   })
        // }
        this.wrapperStyle = this.getAlignment(this.alignment);
        // if (!this.lableById) {
        //   this.lableById = 'lableById' + Math.round(Math.random() * 1000);
        // }
        // Set the initial value of 'ariaLabel'
        this.setLabel();
    }
    setLabel() {
        this.currentAriaLabel = this.ariaLabel.trim() ? this.ariaLabel : '';
        if (this.title && this.title.trim().length > 0) {
            if (!this.ariaLabel.trim()) {
                this.currentAriaLabel = this.title;
            }
        }
    }
    ngAfterViewInit() {
        if (this.style) {
            if (typeof this.style === 'string') {
                this.modalContainer.nativeElement.style.cssText += this.style;
            }
            else {
                Object.assign(this.modalStyle, this.style);
            }
        }
        if (this.contentStyle) {
            if (typeof this.contentStyle === 'string') {
                this.body.nativeElement.style.cssText += this.contentStyle;
            }
            else {
                Object.assign(this.contentStyleObj, this.contentStyle);
            }
        }
        if (this.footerStyle) {
            if (typeof this.footerStyle === 'string' && this.footer) {
                this.footer.nativeElement.style.cssText += this.footerStyle;
            }
            else {
                Object.assign(this.footerStyleObj, this.footerStyle);
            }
        }
        if (this.serviceRef) {
            this.serviceCall = true;
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.component);
            this.modalCompRef = componentFactory.create(this.injector);
            if (this.compValue) {
                for (const prop of Object.keys(this.compValue)) {
                    this.modalCompRef.instance[prop] = this.compValue[prop];
                }
            }
            this.applicationRef.attachView(this.modalCompRef.hostView);
            this.modalContainer.nativeElement.appendChild(this.modalCompRef.location.nativeElement);
            this.showModal(this.appModalId);
        }
    }
    ngAfterContentInit() {
        if (this.templates.length) {
            this.templateMap = {};
        }
        this.templates.forEach((item) => {
            this.templateMap[item.name] = item.template;
        });
    }
    ngOnChanges(changes) {
        if (changes.alignment) {
            this.wrapperStyle = this.getAlignment(changes.alignment.currentValue);
        }
        // Reset 'ariaLabel' when 'title' or 'ariaLabel' change
        if (changes.title || changes.ariaLabel) {
            this.setLabel();
        }
    }
    getAlignment(alignment) {
        let alignItems = 'center';
        let justifyContent = 'center';
        if (alignment === 'top') {
            alignItems = 'flex-start';
        }
        else if (alignment === 'bottom') {
            alignItems = 'flex-end';
        }
        else if (alignment === 'left') {
            justifyContent = 'flex-start';
        }
        else if (alignment === 'right') {
            justifyContent = 'flex-end';
        }
        return { 'align-items': alignItems, 'justify-content': justifyContent };
    }
    showModal(id) {
        this.modalContainerService.open(id).subscribe((data) => {
            if (data) {
                this.needOutlineStyle = true;
                this.onOpen.emit(id);
            }
        });
    }
    close(id) {
        this.modalContainerService.close(id).subscribe((data) => {
            if (data) {
                this.needOutlineStyle = false;
                this.onClose.emit(id);
            }
        });
    }
    closeModal(id, value) {
        this.modalContainerService.close(id);
        this.closeValue.next(value);
        this.closeValue.complete();
    }
    closeModalOk(id) {
        this.modalContainerService.close(id);
        if (this.serviceRef) {
            this.serviceRef.closeModal(id);
        }
        this.onCheckModalOk.emit();
    }
    afterClosed() {
        return this.closeValue;
    }
    wrapperClickHandle(event, id) {
        if (event?.target.closest('.ap-modal') || !event?.target.closest('.ap-modal-wrapper'))
            return;
        if (this.serviceRef && this.maskCloseable) {
            this.serviceRef.closeModal(id);
            return;
        }
        if (this.maskCloseable) {
            this.close(id);
        }
    }
    onKeyDown(event, modalId) {
        if (event.keyCode === 9) { //tab
            this.needOutlineStyle = false;
        }
        else if (event.keyCode === 27) { //esc
            if (this.closeOnPressEscape) {
                this.serviceRef ? this.serviceRef.closeModal(modalId) : this.close(modalId);
            }
        }
    }
    onModalScroll(event) {
        this.onScrollModal.emit(event);
    }
    ngOnDestroy() {
        if (this.modalCompRef) {
            this.modalCompRef.destroy();
        }
        this.globalListenFunc && this.globalListenFunc();
    }
    get _closeAriaLabel() {
        return this.closeAriaLabel || this.closeAriaLabel === ""
            ? this.closeAriaLabel
            : this.localizationConfig.translation ? this.localizationConfig.translation['close'] : null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalComponent, deps: [{ token: i0.ApplicationRef }, { token: ModalContainerService }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.Renderer2 }, { token: i2.DomSanitizer }, { token: i3.LocalizationConfig }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.11", type: ModalComponent, selector: "ap-modal", inputs: { appModalId: "appModalId", title: "title", content: "content", maskCloseable: "maskCloseable", closable: "closable", style: "style", styleClass: "styleClass", contentStyle: "contentStyle", contentStyleClass: "contentStyleClass", footerStyle: "footerStyle", closeOnPressEscape: "closeOnPressEscape", alignment: "alignment", initialFocusIndex: "initialFocusIndex", ariaLabelLedby: "ariaLabelLedby", ariaLabel: "ariaLabel", closeAriaLabel: "closeAriaLabel", closeTabindex: "closeTabindex", blockScroll: "blockScroll", attachedDOM: "attachedDOM" }, outputs: { onCheckModalOk: "onCheckModalOk", onClose: "onClose", onOpen: "onOpen", onScrollModal: "onScrollModal" }, queries: [{ propertyName: "templates", predicate: CustomizeTemplate }], viewQueries: [{ propertyName: "modalContainer", first: true, predicate: ["modalContainer"], descendants: true, read: ElementRef }, { propertyName: "body", first: true, predicate: ["body"], descendants: true, read: ElementRef }, { propertyName: "footer", first: true, predicate: ["footer"], descendants: true, read: ElementRef }], usesOnChanges: true, ngImport: i0, template: "<modal [id]=\"appModalId\" [attr.id]=\"appModalId\" [initialFocusIndex]=\"initialFocusIndex\" [ariaLabelLedby]=\"ariaLabelLedby\" [blockScroll]=\"blockScroll\" [attachedDOM]=\"attachedDOM\">\n\t<div class=\"ap-modal-wrapper\" [ngStyle]=\"wrapperStyle\" (click)=\"wrapperClickHandle($event, appModalId)\">\n\t\t<div #modalContainer class=\"ap-modal\" [class.ap-modal-static]=\"!serviceCall\" role=\"dialog\" aria-modal=\"true\" (keydown)=\"onKeyDown($event,appModalId)\" [attr.aria-label]=\"currentAriaLabel\" [attr.aria-labelledby]=\"'labelledbyId'\" [ngStyle]=\"modalStyle\" [class]=\"styleClass\">\n\t\t\t<!-- <ng-container *ngIf=\"serviceCall\">\n\t\t\t\t<div #body [ngClass]=\"'ap-modal-body'\" [ngStyle]=\"contentStyle\" [class]=\"contentStyleClass\">\n\t\t\t\t\t<ng-content></ng-content>\n\t\t\t\t</div>\n\t\t\t</ng-container> -->\n\t\t\t<span [id]=\"'labelledbyId'\" class=\"mock-labelledby\" aria-hidden=\"true\"></span>\n\t\t\t<ng-container *ngIf=\"!serviceCall\">\n\t\t\t\t<div class=\"ap-modal-header\">\n\t\t\t\t\t<ng-container *ngTemplateOutlet=\"templateMap && templateMap['header']\">\n\t\t\t\t\t</ng-container>\n\t\t\t\t\t<h2 class=\"ap-modal-title\" *ngIf=\"title\">{{title}}</h2>\n\t\t\t\t</div>\n\t\t\t\t<div #body [ngClass]=\"'ap-modal-body'\" [ngStyle]=\"contentStyleObj\" [class]=\"contentStyleClass\" (scroll)=\"onModalScroll($event)\">\n\t\t\t\t\t<ng-content></ng-content>\n\t\t\t\t</div>\n\t\t\t\t<div #footer [ngClass]=\"'ap-modal-footer'\" *ngIf=\"templateMap && templateMap['footer']\" [ngStyle]=\"footerStyleObj\">\n\t\t\t\t\t<div class=\"ap-modal-footer-customize\">\n\t\t\t\t\t\t<ng-container *ngTemplateOutlet=\"templateMap['footer']\"></ng-container>\n\t\t\t\t\t</div>\n\t\t\t\t</div>\n\n\t\t\t\t<div class=\"ap-modal-header-icons-container\">\n\t\t\t\t\t<div class=\"ap-modal-header-icons\" [class.ap-modal-header-icons-focus]=\"needOutlineStyle\">\n\t\t\t\t\t\t<ng-container *ngTemplateOutlet=\"templateMap && templateMap['icons']\"></ng-container>\n\t\t\t\t\t</div>\n\t\t\t\t\t<div *ngIf=\"closable\" class=\"ap-modal-header-close-wrapper\">\n\t\t\t\t\t\t<div role=\"button\" [tabindex]=\"closeTabindex\" [attr.aria-label]=\"_closeAriaLabel\" class=\"ap-modal-header-icon ap-modal-header-close\" (click)=\"close(appModalId)\" (keydown.enter)=\"close(appModalId)\">\n\t\t\t\t\t\t\t<span class=\"Appkit4-icon icon-close-outline\"></span>\n\t\t\t\t\t\t</div>\n\t\t\t\t\t</div>\n\t\t\t\t</div>\n\t\t\t</ng-container>\n\t\t</div>\n\t</div>\n</modal>\n\n\n", styles: [".cdk-overlay-container,.cdk-global-overlay-wrapper{pointer-events:none;top:0;left:0;height:100%;width:100%}.cdk-overlay-container{position:fixed;z-index:1000}.cdk-overlay-container:empty{display:none}.cdk-global-overlay-wrapper{display:flex;position:absolute;z-index:1000}.cdk-overlay-pane{position:absolute;pointer-events:auto;box-sizing:border-box;z-index:1000;display:flex;max-width:100%;max-height:100%}.cdk-overlay-backdrop{position:absolute;inset:0;z-index:1000;pointer-events:auto;-webkit-tap-highlight-color:rgba(0,0,0,0);transition:opacity .4s cubic-bezier(.25,.8,.25,1);opacity:0}.cdk-overlay-backdrop.cdk-overlay-backdrop-showing{opacity:1}.cdk-high-contrast-active .cdk-overlay-backdrop.cdk-overlay-backdrop-showing{opacity:.6}.cdk-overlay-dark-backdrop{background:#00000052}.cdk-overlay-transparent-backdrop{transition:visibility 1ms linear,opacity 1ms linear;visibility:hidden;opacity:1}.cdk-overlay-transparent-backdrop.cdk-overlay-backdrop-showing{opacity:0;visibility:visible}.cdk-overlay-backdrop-noop-animation{transition:none}.cdk-overlay-connected-position-bounding-box{position:absolute;z-index:1000;display:flex;flex-direction:column;min-width:1px;min-height:1px}.cdk-global-scrollblock{position:fixed;width:100%;overflow-y:scroll}::ng-deep modal{font-family:PwC Helvetica Neue,sans-serif;visibility:hidden}::ng-deep #labelledbyId{display:block;height:0rem}::ng-deep .ap-modal-wrapper{position:fixed;display:flex;inset:0;outline:0;z-index:2000;-webkit-backdrop-filter:blur(.25rem);backdrop-filter:blur(.25rem);background-color:rgba(var(--color-background-mask-rgb),var(--opacity-6));overflow:auto;opacity:0;transition:opacity .3s,visibility .3s}::ng-deep .ap-modal-wrapper.show{opacity:1;visibility:visible}::ng-deep .ap-modal-wrapper *{scrollbar-width:none}::ng-deep .ap-modal-wrapper *::-webkit-scrollbar{width:0px;height:0px}::ng-deep .ap-modal{position:relative;background-color:var(--color-background-container);box-shadow:var(--box-shadow-3);border-radius:var(--border-radius-3);font-size:1rem;transition:transform .3s;max-height:100vh;max-width:100%;transform:translateY(.625rem)}::ng-deep .ap-modal.ap-modal-static{display:flex;flex-direction:column}::ng-deep .ap-modal .mock-labelledby{height:0;opacity:0;width:0}::ng-deep .ap-modal-header{display:flex;align-items:center;justify-content:space-between;text-align:left;padding:var(--spacing-6);min-height:4rem}::ng-deep .ap-modal-header .ap-badge{margin-right:var(--spacing-3)}::ng-deep .ap-modal-header .ap-modal-title{font-size:var(--spacing-6);font-weight:var(--font-weight-2);line-height:1.5rem;letter-spacing:-.025rem;color:var(--color-text-heading);margin:0 auto 0 0}::ng-deep .ap-modal-body{padding:0 var(--spacing-6) var(--spacing-6) var(--spacing-6);overflow:auto}::ng-deep .ap-modal-body p{margin:0;font-size:1rem;line-height:1.5rem;letter-spacing:-.025rem;color:var(--color-text-body)}::ng-deep .ap-modal-footer{display:flex;justify-content:flex-end;padding:var(--spacing-3) var(--spacing-6) var(--spacing-6) var(--spacing-6);margin-top:-.5rem;min-height:4rem;overflow:auto}::ng-deep .ap-modal-footer .ap-button-secondary{margin-right:var(--spacing-3)}::ng-deep .ap-modal-footer-customize{display:flex}::ng-deep .ap-modal-header-icons-container{position:absolute;top:var(--spacing-4);right:var(--spacing-4);display:flex;gap:var(--spacing-2)}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icons{display:flex;align-items:center}::ng-deep .ap-modal-header-icons-container .ap-modal-header-close-wrapper{display:flex;align-items:center;padding:var(--spacing-2);margin:calc(-1 * var(--spacing-2));overflow:hidden}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icons-focus>:first-child:focus{outline:-webkit-focus-ring-color auto .3125rem}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icon{position:relative;width:2.5rem;height:2.5rem;padding:var(--spacing-3);background-color:transparent}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icon:hover{background-color:var(--color-background-hover);border-radius:var(--border-radius-2);cursor:pointer}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icon .Appkit4-icon{color:var(--color-text-heading)}::ng-deep .ap-modal-wrapper.show .ap-modal{transform:translateY(0)}::ng-deep body.modal-open{overflow:hidden}\n"], dependencies: [{ kind: "directive", type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i4.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: ModalContainerComponent, selector: "modal", inputs: ["id", "initialFocusIndex", "blockScroll", "attachedDOM", "ariaLabelLedby"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ap-modal', template: "<modal [id]=\"appModalId\" [attr.id]=\"appModalId\" [initialFocusIndex]=\"initialFocusIndex\" [ariaLabelLedby]=\"ariaLabelLedby\" [blockScroll]=\"blockScroll\" [attachedDOM]=\"attachedDOM\">\n\t<div class=\"ap-modal-wrapper\" [ngStyle]=\"wrapperStyle\" (click)=\"wrapperClickHandle($event, appModalId)\">\n\t\t<div #modalContainer class=\"ap-modal\" [class.ap-modal-static]=\"!serviceCall\" role=\"dialog\" aria-modal=\"true\" (keydown)=\"onKeyDown($event,appModalId)\" [attr.aria-label]=\"currentAriaLabel\" [attr.aria-labelledby]=\"'labelledbyId'\" [ngStyle]=\"modalStyle\" [class]=\"styleClass\">\n\t\t\t<!-- <ng-container *ngIf=\"serviceCall\">\n\t\t\t\t<div #body [ngClass]=\"'ap-modal-body'\" [ngStyle]=\"contentStyle\" [class]=\"contentStyleClass\">\n\t\t\t\t\t<ng-content></ng-content>\n\t\t\t\t</div>\n\t\t\t</ng-container> -->\n\t\t\t<span [id]=\"'labelledbyId'\" class=\"mock-labelledby\" aria-hidden=\"true\"></span>\n\t\t\t<ng-container *ngIf=\"!serviceCall\">\n\t\t\t\t<div class=\"ap-modal-header\">\n\t\t\t\t\t<ng-container *ngTemplateOutlet=\"templateMap && templateMap['header']\">\n\t\t\t\t\t</ng-container>\n\t\t\t\t\t<h2 class=\"ap-modal-title\" *ngIf=\"title\">{{title}}</h2>\n\t\t\t\t</div>\n\t\t\t\t<div #body [ngClass]=\"'ap-modal-body'\" [ngStyle]=\"contentStyleObj\" [class]=\"contentStyleClass\" (scroll)=\"onModalScroll($event)\">\n\t\t\t\t\t<ng-content></ng-content>\n\t\t\t\t</div>\n\t\t\t\t<div #footer [ngClass]=\"'ap-modal-footer'\" *ngIf=\"templateMap && templateMap['footer']\" [ngStyle]=\"footerStyleObj\">\n\t\t\t\t\t<div class=\"ap-modal-footer-customize\">\n\t\t\t\t\t\t<ng-container *ngTemplateOutlet=\"templateMap['footer']\"></ng-container>\n\t\t\t\t\t</div>\n\t\t\t\t</div>\n\n\t\t\t\t<div class=\"ap-modal-header-icons-container\">\n\t\t\t\t\t<div class=\"ap-modal-header-icons\" [class.ap-modal-header-icons-focus]=\"needOutlineStyle\">\n\t\t\t\t\t\t<ng-container *ngTemplateOutlet=\"templateMap && templateMap['icons']\"></ng-container>\n\t\t\t\t\t</div>\n\t\t\t\t\t<div *ngIf=\"closable\" class=\"ap-modal-header-close-wrapper\">\n\t\t\t\t\t\t<div role=\"button\" [tabindex]=\"closeTabindex\" [attr.aria-label]=\"_closeAriaLabel\" class=\"ap-modal-header-icon ap-modal-header-close\" (click)=\"close(appModalId)\" (keydown.enter)=\"close(appModalId)\">\n\t\t\t\t\t\t\t<span class=\"Appkit4-icon icon-close-outline\"></span>\n\t\t\t\t\t\t</div>\n\t\t\t\t\t</div>\n\t\t\t\t</div>\n\t\t\t</ng-container>\n\t\t</div>\n\t</div>\n</modal>\n\n\n", styles: [".cdk-overlay-container,.cdk-global-overlay-wrapper{pointer-events:none;top:0;left:0;height:100%;width:100%}.cdk-overlay-container{position:fixed;z-index:1000}.cdk-overlay-container:empty{display:none}.cdk-global-overlay-wrapper{display:flex;position:absolute;z-index:1000}.cdk-overlay-pane{position:absolute;pointer-events:auto;box-sizing:border-box;z-index:1000;display:flex;max-width:100%;max-height:100%}.cdk-overlay-backdrop{position:absolute;inset:0;z-index:1000;pointer-events:auto;-webkit-tap-highlight-color:rgba(0,0,0,0);transition:opacity .4s cubic-bezier(.25,.8,.25,1);opacity:0}.cdk-overlay-backdrop.cdk-overlay-backdrop-showing{opacity:1}.cdk-high-contrast-active .cdk-overlay-backdrop.cdk-overlay-backdrop-showing{opacity:.6}.cdk-overlay-dark-backdrop{background:#00000052}.cdk-overlay-transparent-backdrop{transition:visibility 1ms linear,opacity 1ms linear;visibility:hidden;opacity:1}.cdk-overlay-transparent-backdrop.cdk-overlay-backdrop-showing{opacity:0;visibility:visible}.cdk-overlay-backdrop-noop-animation{transition:none}.cdk-overlay-connected-position-bounding-box{position:absolute;z-index:1000;display:flex;flex-direction:column;min-width:1px;min-height:1px}.cdk-global-scrollblock{position:fixed;width:100%;overflow-y:scroll}::ng-deep modal{font-family:PwC Helvetica Neue,sans-serif;visibility:hidden}::ng-deep #labelledbyId{display:block;height:0rem}::ng-deep .ap-modal-wrapper{position:fixed;display:flex;inset:0;outline:0;z-index:2000;-webkit-backdrop-filter:blur(.25rem);backdrop-filter:blur(.25rem);background-color:rgba(var(--color-background-mask-rgb),var(--opacity-6));overflow:auto;opacity:0;transition:opacity .3s,visibility .3s}::ng-deep .ap-modal-wrapper.show{opacity:1;visibility:visible}::ng-deep .ap-modal-wrapper *{scrollbar-width:none}::ng-deep .ap-modal-wrapper *::-webkit-scrollbar{width:0px;height:0px}::ng-deep .ap-modal{position:relative;background-color:var(--color-background-container);box-shadow:var(--box-shadow-3);border-radius:var(--border-radius-3);font-size:1rem;transition:transform .3s;max-height:100vh;max-width:100%;transform:translateY(.625rem)}::ng-deep .ap-modal.ap-modal-static{display:flex;flex-direction:column}::ng-deep .ap-modal .mock-labelledby{height:0;opacity:0;width:0}::ng-deep .ap-modal-header{display:flex;align-items:center;justify-content:space-between;text-align:left;padding:var(--spacing-6);min-height:4rem}::ng-deep .ap-modal-header .ap-badge{margin-right:var(--spacing-3)}::ng-deep .ap-modal-header .ap-modal-title{font-size:var(--spacing-6);font-weight:var(--font-weight-2);line-height:1.5rem;letter-spacing:-.025rem;color:var(--color-text-heading);margin:0 auto 0 0}::ng-deep .ap-modal-body{padding:0 var(--spacing-6) var(--spacing-6) var(--spacing-6);overflow:auto}::ng-deep .ap-modal-body p{margin:0;font-size:1rem;line-height:1.5rem;letter-spacing:-.025rem;color:var(--color-text-body)}::ng-deep .ap-modal-footer{display:flex;justify-content:flex-end;padding:var(--spacing-3) var(--spacing-6) var(--spacing-6) var(--spacing-6);margin-top:-.5rem;min-height:4rem;overflow:auto}::ng-deep .ap-modal-footer .ap-button-secondary{margin-right:var(--spacing-3)}::ng-deep .ap-modal-footer-customize{display:flex}::ng-deep .ap-modal-header-icons-container{position:absolute;top:var(--spacing-4);right:var(--spacing-4);display:flex;gap:var(--spacing-2)}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icons{display:flex;align-items:center}::ng-deep .ap-modal-header-icons-container .ap-modal-header-close-wrapper{display:flex;align-items:center;padding:var(--spacing-2);margin:calc(-1 * var(--spacing-2));overflow:hidden}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icons-focus>:first-child:focus{outline:-webkit-focus-ring-color auto .3125rem}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icon{position:relative;width:2.5rem;height:2.5rem;padding:var(--spacing-3);background-color:transparent}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icon:hover{background-color:var(--color-background-hover);border-radius:var(--border-radius-2);cursor:pointer}::ng-deep .ap-modal-header-icons-container .ap-modal-header-icon .Appkit4-icon{color:var(--color-text-heading)}::ng-deep .ap-modal-wrapper.show .ap-modal{transform:translateY(0)}::ng-deep body.modal-open{overflow:hidden}\n"] }]
        }], ctorParameters: () => [{ type: i0.ApplicationRef }, { type: ModalContainerService }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.Renderer2 }, { type: i2.DomSanitizer }, { type: i3.LocalizationConfig }], propDecorators: { appModalId: [{
                type: Input
            }], title: [{
                type: Input
            }], content: [{
                type: Input
            }], maskCloseable: [{
                type: Input
            }], closable: [{
                type: Input
            }], style: [{
                type: Input
            }], styleClass: [{
                type: Input
            }], contentStyle: [{
                type: Input
            }], contentStyleClass: [{
                type: Input
            }], footerStyle: [{
                type: Input
            }], closeOnPressEscape: [{
                type: Input
            }], alignment: [{
                type: Input
            }], initialFocusIndex: [{
                type: Input
            }], ariaLabelLedby: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], closeAriaLabel: [{
                type: Input
            }], closeTabindex: [{
                type: Input
            }], blockScroll: [{
                type: Input
            }], attachedDOM: [{
                type: Input
            }], onCheckModalOk: [{
                type: Output
            }], onClose: [{
                type: Output
            }], onOpen: [{
                type: Output
            }], onScrollModal: [{
                type: Output
            }], templates: [{
                type: ContentChildren,
                args: [CustomizeTemplate]
            }], modalContainer: [{
                type: ViewChild,
                args: ['modalContainer', { read: ElementRef }]
            }], body: [{
                type: ViewChild,
                args: ['body', { read: ElementRef }]
            }], footer: [{
                type: ViewChild,
                args: ['footer', { read: ElementRef }]
            }] } });

/**
 * To creating the dynamic modal using service
 *
 * @export
 * @class DynamicDialogService
 */
class DynamicDialogService {
    constructor(componentFactoryResolver, injector, applicationRef, modalContainerService, overlay) {
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this.applicationRef = applicationRef;
        this.modalContainerService = modalContainerService;
        this.overlay = overlay;
        this.modalCompList = {};
        this.positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();
    }
    /**
     * To open a modal dialog containing the given component.
     *
     * @param {*} component The component to load into the modal
     * @param {*} [options] passing the `appModalId` and other built-in properties of modal
     * @param {*} [compValue={}] passing properties to the custom component
     * @return {*} `ComponentRef` created by a `ComponentFactory`
     * @memberof DynamicDialogService
     */
    openModal(component, options, compValue = {}) {
        let domPortal;
        this.overlayRef = this.overlay.create({
            positionStrategy: this.positionStrategy,
            hasBackdrop: false
        });
        if (component) {
            if (options.appModalId) {
                const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ModalComponent);
                const modalRef = componentFactory.create(this.injector);
                for (const prop of Object.keys(options)) {
                    modalRef.instance[prop] = options[prop];
                }
                modalRef.instance.appModalId = options.appModalId;
                modalRef.instance.component = component;
                modalRef.instance.compValue = compValue;
                modalRef.instance.serviceRef = this;
                modalRef.instance.serviceCall = true;
                modalRef.instance.event = window.event;
                this.modalCompList[options.appModalId] = modalRef;
                this.applicationRef.attachView(modalRef.hostView);
                document.body.appendChild(modalRef.location.nativeElement);
                this.applicationRef.tick();
                const modalEle = document.querySelector('#' + options.appModalId);
                domPortal = new DomPortal(modalEle);
                this.overlayRef.attach(domPortal);
                this.overlayRef.outsidePointerEvents().subscribe(() => {
                    this.overlayRef.detach();
                    this.overlayRef.dispose();
                    this.overlayRef.detachBackdrop();
                });
                this.applicationRef.tick();
                return modalRef;
            }
            else {
                console.error('Please pass the appModalId attribute');
                return null;
            }
        }
        else {
            console.error('Please pass the component');
            return null;
        }
    }
    /**
     * To close the modal using service by passing the appModalId
     *
     * @param {*} id
     * @param {*} value Optional result to return to the modal opener.
     * @return {*} An `Observable` of the `id`.
     * @memberof DynamicDialogService
     */
    closeModal(id, value) {
        if (this.modalCompList[id]) {
            this.modalCompList[id].instance.closeModal(id, value);
            let closeSubject$ = new Subject();
            setTimeout(() => {
                if (this.modalCompList[id]) {
                    // clear overlay reference
                    this.overlayRef.detach();
                    this.overlayRef.dispose();
                    this.overlayRef.detachBackdrop();
                    // clear modal reference
                    this.modalCompList[id].destroy();
                    delete this.modalCompList[id];
                    closeSubject$.next(id);
                    closeSubject$.complete();
                }
                else {
                    closeSubject$.error({
                        appModalId: id,
                        error: `There is no modal with appModalId '${id}'`,
                    });
                }
            }, 300);
            return closeSubject$.asObservable();
        }
        else {
            return new Observable((observer) => {
                observer.error({
                    appModalId: id,
                    error: `There is no modal with appModalId '${id}'`,
                });
            });
        }
    }
    /**
     * Close all the dynamic component
     * @return {*} An `Observable` of the closed modals' `id`.
     * @memberof DynamicDialogService
     */
    closeAll() {
        let modalObservables = [];
        for (let index = 0; index < Object.keys(this.modalCompList).length; index++) {
            const id = Object.keys(this.modalCompList)[index];
            if (id) {
                modalObservables.push(this.closeModal(id));
            }
        }
        return forkJoin(modalObservables);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: DynamicDialogService, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: ModalContainerService }, { token: i2$1.Overlay }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: DynamicDialogService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: DynamicDialogService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: ModalContainerService }, { type: i2$1.Overlay }] });

class ModalCloseDirective {
    constructor(dynamicDialogService) {
        this.dynamicDialogService = dynamicDialogService;
    }
    onClick(event) {
        this.dynamicDialogService.closeModal(this.modalResult.appModalId, this.modalResult.value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalCloseDirective, deps: [{ token: DynamicDialogService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.11", type: ModalCloseDirective, selector: "[ap-modal-close]", inputs: { modalResult: ["ap-modal-close", "modalResult"] }, host: { listeners: { "click": "onClick($event)", "keydown.enter": "onClick($event)" } }, exportAs: ["ap-modal-close"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalCloseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ap-modal-close]',
                    exportAs: 'ap-modal-close',
                    host: {
                        '(click)': 'onClick($event)',
                        '(keydown.enter)': 'onClick($event)'
                    }
                }]
        }], ctorParameters: () => [{ type: DynamicDialogService }], propDecorators: { modalResult: [{
                type: Input,
                args: ['ap-modal-close']
            }] } });

class ModalModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.11", ngImport: i0, type: ModalModule, declarations: [ModalComponent, ModalContainerComponent, CustomizeTemplate, ModalCloseDirective], imports: [CommonModule, OverlayModule], exports: [ModalComponent, ModalContainerComponent, CustomizeTemplate, ModalCloseDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalModule, providers: [ModalContainerService, DynamicDialogService], imports: [CommonModule, OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule, OverlayModule,
                    ],
                    declarations: [ModalComponent, ModalContainerComponent, CustomizeTemplate, ModalCloseDirective],
                    exports: [ModalComponent, ModalContainerComponent, CustomizeTemplate, ModalCloseDirective],
                    providers: [ModalContainerService, DynamicDialogService]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CustomizeTemplate, DynamicDialogService, ModalCloseDirective, ModalComponent, ModalContainerComponent, ModalContainerService, ModalModule };
